import { IconCheck, IconChevronDown } from '@developerz/ui';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { OrgRole } from '../lib/org-members';
import { Popover } from './Popover';
import { StatusPill } from './StatusPill';

// A member's role in the Status-chip voice (spec §3.2, neutral tone). Rows the
// caller can't manage get a static <StatusPill>; manageable rows get the same
// chip as a menu trigger — the RBAC role-change control a native <select> can't
// style. Menu mechanics are <Popover/>'s (outside click, Escape, focus back to
// the chip). The parent owns the mutation (and its busy/refetch), so this
// component only reports the chosen role; `busy` holds the chip shut while
// that write is in flight.
// `.role-pill--trigger` / `.role-pill__option` are stable test hooks.
export function RolePill(props: {
  readonly role: OrgRole;
  readonly editable: boolean;
  readonly options: readonly OrgRole[];
  readonly busy: boolean;
  readonly login: string;
  readonly onChange: (role: OrgRole) => void;
}): JSX.Element {
  return (
    <Show
      when={props.editable}
      fallback={<StatusPill tone="neutral">{t(`org_members.role_${props.role}`)}</StatusPill>}
    >
      <Popover
        class="role-pill"
        label={t('org_members.change_role_aria', { login: props.login })}
        triggerClass="badge status badge--secondary role-pill--trigger"
        panelClass="role-pill__menu"
        busy={props.busy}
        trigger={
          <>
            {t(`org_members.role_${props.role}`)}
            <IconChevronDown />
          </>
        }
      >
        {(close) => (
          <ul>
            <For each={props.options}>
              {(r) => (
                <li>
                  <button
                    type="button"
                    class="role-pill__option"
                    data-role={r}
                    aria-current={r === props.role ? 'true' : undefined}
                    onClick={() => {
                      close();
                      if (r !== props.role) props.onChange(r);
                    }}
                  >
                    {t(`org_members.role_${r}`)}
                    <Show when={r === props.role}>
                      <IconCheck />
                    </Show>
                  </button>
                </li>
              )}
            </For>
          </ul>
        )}
      </Popover>
    </Show>
  );
}
